/**
 * Eyebrow — chip d'amorce standardisée du profil Particulier.
 *
 * Variants :
 *  - `default` : light (sur fond paper) — utilisée dans les heros / sections claires
 *  - `dark`    : sur fond noir (CTA dark immersif)
 *  - `mono`    : version monospace techy (utilisée dans les bandeaux de stats)
 */

import type { ReactNode } from 'react';
import { cn } from '../../../lib/cn';
import { getIcon } from '../../../lib/lucide-icons';
import { PARTICULIER_PILL } from '../../../config/audience/particulier.ui';

export interface ParticulierEyebrowProps {
  /** Texte de la pill */
  children: ReactNode;
  /** Variant visuel */
  variant?: 'default' | 'dark' | 'mono';
  /** Affiche un live-dot à gauche */
  withDot?: boolean;
  /** Nom d'icône Lucide à afficher avant le texte */
  iconName?: string;
  /** classes supplémentaires */
  className?: string;
}

export function ParticulierEyebrow({
  children,
  variant = 'default',
  withDot = false,
  iconName,
  className,
}: ParticulierEyebrowProps) {
  const Icon = iconName ? getIcon(iconName) : null;

  if (variant === 'dark') {
    return (
      <span className={cn(PARTICULIER_PILL.eyebrowDark, className)}>
        {withDot && (
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-glow-pulse rounded-full bg-accent-400/70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-400" />
          </span>
        )}
        {Icon && <Icon className="h-3 w-3" strokeWidth={2} />}
        <span>{children}</span>
      </span>
    );
  }

  if (variant === 'mono') {
    return (
      <p
        className={cn(
          'font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500',
          className,
        )}
      >
        {children}
      </p>
    );
  }

  return (
    <span className={cn(PARTICULIER_PILL.eyebrow, className)}>
      {withDot && <span className="live-dot" aria-hidden />}
      {Icon && <Icon className="h-3 w-3" strokeWidth={2} />}
      <span>{children}</span>
    </span>
  );
}
